"use client";

import Link from "next/link";
import { CalendarDays, History, Loader2, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import ProfileRouteShell from "@/components/user-button-pages/ProfileRouteShell";
import { useEffectivePass } from "@/hooks/useEffectivePass";
import ScheduleCalendar from "./ScheduleCalendar";
import ScheduleGuestMock from "./ScheduleGuestMock";
import { useScheduleData, type ScheduleData } from "./useScheduleData";

/**
 * 학습 일정 관리 캘린더 — /my/schedule (JCG-2310).
 *
 * 구독한 학습 계획(이론 4주·코딩 6주)을 통합 캘린더로 보여주고 개인화한다. 주/월 뷰 토글,
 * DnD 일정 조정(주 뷰), 통합 색 구분(이론=blue/코딩=green), 출석·완료 연동.
 *
 * 게이트(ProfileRouteShell 이 로그인 가드 담당):
 *   - 비로그인 → guestPreview(정적 목업) + 로그인 CTA
 *   - 로그인 O + Starter 미만(isAdFree=false) → 잠금/업셀(/membership)
 *   - 로그인 O + Starter+ + 구독 0개 → 정규 날짜 미리보기 + 시작 CTA(ScheduleCalendar 내부 분기)
 *   - 로그인 O + Starter+ + 구독 1+ → 전체 캘린더
 *
 * 캘린더는 넓게 쓰므로 우측 맥락 컬럼 없이(wide). route-layout 의 isMyTwoColumnPage 에 포함됨.
 */
export default function SchedulePage() {
  return (
    <ProfileRouteShell
      title="학습 일정"
      wide
      guestPreview={<ScheduleGuestMock />}
      guestDescription="로그인하면 이론·코딩 학습 계획을 캘린더로 관리하고 일정을 조정할 수 있어요."
    >
      <ScheduleContent />
    </ProfileRouteShell>
  );
}

/** 로그인 이후 본문 — Starter 게이트 + 캘린더 데이터 로드. */
function ScheduleContent() {
  const { isAdFree, isLoaded } = useEffectivePass();

  // 게이트 통과(Starter+) 한 경우에만 데이터 fetch.
  const data = useScheduleData(isLoaded && isAdFree);

  if (!isLoaded) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Loader2
          className="h-6 w-6 animate-spin text-muted-foreground"
          aria-hidden
        />
      </div>
    );
  }

  // Starter 미만: 잠금 업셀.
  if (!isAdFree) {
    return <ScheduleLock />;
  }

  return (
    <div className="space-y-4">
      <Header data={data} />
      {data.loadError ? (
        <p
          role="alert"
          className="py-8 text-center text-sm text-muted-foreground"
        >
          {data.loadError}
        </p>
      ) : data.loading ? (
        <div
          className="flex min-h-[40vh] items-center justify-center gap-2 text-sm text-muted-foreground"
          aria-live="polite"
          aria-busy="true"
        >
          <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
          학습 일정 불러오는 중…
        </div>
      ) : (
        <>
          {data.readOnly && (
            <div
              className="flex items-start gap-2 rounded-lg border border-border bg-muted/50 px-3 py-2.5 text-sm text-muted-foreground"
              role="status"
            >
              <History className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              <p>
                <span className="font-medium text-foreground">
                  {data.selectedExamSessionLabel} 일정
                </span>
                은 이전 회차 기록으로, 조회만 가능해요.
              </p>
            </div>
          )}
          <ScheduleCalendar key={data.selectedExamSessionId} data={data} />
        </>
      )}
    </div>
  );
}

function Header({ data }: { data?: ScheduleData }) {
  const showSessionSelect = (data?.availableSessions.length ?? 0) > 1;

  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div className="flex items-center gap-2">
        <CalendarDays className="h-5 w-5 text-amber-500" aria-hidden />
        <h2 className="text-xl font-bold text-foreground">학습 일정</h2>
      </div>

      {data && showSessionSelect && (
        <Select
          value={data.selectedExamSessionId}
          onValueChange={(examSessionId) => {
            void data.selectSession(examSessionId);
          }}
          disabled={data.loading || data.writePending}
        >
          <SelectTrigger
            aria-label="학습 일정 회차 선택"
            className="h-8 w-[168px] text-xs sm:w-[180px]"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent align="end">
            {data.availableSessions.map((session) => (
              <SelectItem key={session.id} value={session.id}>
                {session.label} · {session.isActive ? "현재" : "이전"}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}

/** Starter 미만 잠금/업셀(DashboardTodaySchedule 잠금 카드와 톤 일치). */
function ScheduleLock() {
  return (
    <div className="space-y-4">
      <Header />
      <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-border bg-card px-6 py-14 text-center">
        <span
          className="flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 text-amber-600 dark:bg-amber-950/60 dark:text-amber-300"
          aria-hidden
        >
          <Lock className="h-6 w-6" />
        </span>
        <div>
          <p className="text-base font-bold text-foreground">
            Starter 이용권으로 개인 학습 일정을 관리해요
          </p>
          <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
            이론 4주·코딩 6주 계획을 캘린더로 모아 보고, 일정을 내 페이스에 맞게
            조정하고, 출석·완료를 함께 기록할 수 있어요.
          </p>
        </div>
        <Button asChild className="mt-1">
          <Link href="/membership">Starter 이용권 보기</Link>
        </Button>
      </div>
    </div>
  );
}
